import { useEffect, useMemo, useRef, useState } from 'react'

import { Agreement01Icon } from '@hugeicons/react'
import clsx from 'clsx'

import {
  sortPartnersForHomePreview
} from '@/features/partners/lib/selectHomeCheckInPartners'
import { useNavigation } from '@/shared/hooks/useNavigation'
import Image from '@/shared/ui/AppImage'
import {
  FeaturedListCardStrip,
  featuredListCardClassName
} from '@/shared/ui/FeaturedListCardStrip'

type HomePartnerMiniRecord = {
  id: number
  name?: string | null
  logo?: { file?: { url?: string | null } | null } | null
}

const ROTATE_MS = 8000
const ROTATE_JITTER_MS = 500
const SLOT_STAGGER_MS = 250
const FADE_MS = 450

const STRIP_ROW_CLASS = 'h-5 min-h-5 shrink-0 overflow-hidden'

const LOGO_ROW_CLASS =
  'relative w-full shrink-0 overflow-hidden rounded-[0.625rem] border border-brand/10 bg-white aspect-[4/3]'

const LOGO_IMAGE_CLASS = 'h-full w-full object-cover object-center'

const NAME_ROW_CLASS =
  'flex h-8 shrink-0 items-end justify-center overflow-hidden'

const randomBetween = (min: number, max: number) =>
  min + Math.floor(Math.random() * (max - min + 1))

const pickNextPartnerIndex = (
  pool: HomePartnerMiniRecord[],
  currentIndices: number[],
  slotIdx: number
) => {
  if (pool.length <= 1) return currentIndices[slotIdx] ?? 0

  const visibleIds = new Set(
    currentIndices
      .map((poolIndex, index) =>
        index !== slotIdx ? pool[poolIndex]?.id : null
      )
      .filter((id): id is number => id != null)
  )

  for (let step = 1; step < pool.length; step++) {
    const candidateIndex = (currentIndices[slotIdx] + step) % pool.length
    const candidate = pool[candidateIndex]

    if (candidate && !visibleIds.has(candidate.id)) {
      return candidateIndex
    }
  }

  return (currentIndices[slotIdx] + 1) % pool.length
}

const PartnerMiniCard = ({
  partner,
  isFeatured,
  hasStripSlot,
  onClick
}: {
  partner: HomePartnerMiniRecord
  isFeatured: boolean
  hasStripSlot: boolean
  onClick: () => void
}) => {
  const logoUrl = partner.logo?.file?.url

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={
        isFeatured
          ? `${partner.name ?? 'Parceiro'}, top curtidas`
          : (partner.name ?? 'Parceiro')
      }
      className={clsx(
        'flex w-full flex-col overflow-hidden rounded-[0.625rem] border text-left transition-colors',
        isFeatured
          ? featuredListCardClassName('partner')
          : 'border-brand/10 bg-[color-mix(in_srgb,var(--color-brand)_6%,white)] hover:bg-[color-mix(in_srgb,var(--color-brand)_10%,white)]'
      )}
    >
      {hasStripSlot ? (
        <div className={STRIP_ROW_CLASS} aria-hidden={!isFeatured}>
          {isFeatured ? (
            <FeaturedListCardStrip
              label="Top curtidas"
              tone="partner"
              compact
              className="h-full"
            />
          ) : null}
        </div>
      ) : null}

      <span
        className={clsx(
          'flex flex-col gap-1.5 px-2 pb-2',
          hasStripSlot ? 'pt-1' : 'pt-1.5'
        )}
      >
        <span className={LOGO_ROW_CLASS}>
          {logoUrl ? (
            <Image src={logoUrl} alt="" className={LOGO_IMAGE_CLASS} />
          ) : (
            <span className="flex h-full w-full items-center justify-center text-brand">
              <Agreement01Icon
                size={28}
                variant="duotone"
                className="shrink-0"
                aria-hidden
              />
            </span>
          )}
        </span>

        <span className={NAME_ROW_CLASS}>
          <span
            className={clsx(
              'line-clamp-2 w-full text-center text-xs font-semibold leading-tight',
              isFeatured ? 'text-gray-900' : 'text-gray-700'
            )}
          >
            {partner.name}
          </span>
        </span>
      </span>
    </button>
  )
}

const PartnerMiniCardSlot = ({
  partner,
  isFeatured,
  hasStripSlot,
  animate,
  onClick
}: {
  partner: HomePartnerMiniRecord
  isFeatured: boolean
  hasStripSlot: boolean
  animate: boolean
  onClick: () => void
}) => {
  const [displayedPartner, setDisplayedPartner] = useState(partner)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    if (!animate || isFeatured || partner.id === displayedPartner.id) {
      setDisplayedPartner(partner)
      setVisible(true)
      return
    }

    setVisible(false)

    const timer = window.setTimeout(() => {
      setDisplayedPartner(partner)
      setVisible(true)
    }, FADE_MS)

    return () => window.clearTimeout(timer)
  }, [animate, displayedPartner.id, isFeatured, partner])

  return (
    <li
      className={clsx(
        animate &&
          !isFeatured &&
          'transition-opacity duration-[450ms] ease-in-out motion-reduce:transition-none',
        visible ? 'opacity-100' : 'opacity-0',
        !visible && 'pointer-events-none'
      )}
      aria-hidden={!visible}
    >
      <PartnerMiniCard
        partner={displayedPartner}
        isFeatured={isFeatured}
        hasStripSlot={hasStripSlot}
        onClick={onClick}
      />
    </li>
  )
}

type HomePartnersMiniGridProps = {
  partners: HomePartnerMiniRecord[]
  topLikeIds: number[]
  featuredId: number | null
  limit: number
}

export const HomePartnersMiniGrid = ({
  partners,
  topLikeIds,
  featuredId,
  limit
}: HomePartnersMiniGridProps) => {
  const { navigateTo } = useNavigation()
  const hasStripSlot = featuredId != null

  const { featured, rotatingPool } = useMemo(() => {
    const sorted = sortPartnersForHomePreview(partners, topLikeIds)
    const featuredPartner =
      featuredId != null
        ? (sorted.find(partner => partner.id === featuredId) ?? null)
        : null

    return {
      featured: featuredPartner,
      rotatingPool: featuredPartner
        ? sorted.filter(partner => partner.id !== featuredPartner.id)
        : sorted
    }
  }, [featuredId, partners, topLikeIds])

  const rotatingSlots = featured != null ? Math.max(0, limit - 1) : limit
  const shouldRotate = rotatingPool.length > rotatingSlots

  const [slotPoolIndices, setSlotPoolIndices] = useState<number[]>([])

  useEffect(() => {
    setSlotPoolIndices(
      Array.from({ length: rotatingSlots }, (_, index) => index % rotatingPool.length)
    )
  }, [rotatingPool, rotatingSlots])

  const slotPoolIndicesRef = useRef(slotPoolIndices)
  slotPoolIndicesRef.current = slotPoolIndices

  const rotatingPoolRef = useRef(rotatingPool)
  rotatingPoolRef.current = rotatingPool

  useEffect(() => {
    if (!shouldRotate || rotatingSlots === 0) return

    const timers: number[] = []

    const scheduleSlotRotation = (slotIdx: number) => {
      const delay = ROTATE_MS + randomBetween(0, ROTATE_JITTER_MS)

      const timer = window.setTimeout(() => {
        setSlotPoolIndices(current => {
          const pool = rotatingPoolRef.current
          if (pool.length <= rotatingSlots) return current

          const next = [...current]
          next[slotIdx] = pickNextPartnerIndex(pool, current, slotIdx)
          return next
        })

        scheduleSlotRotation(slotIdx)
      }, delay)

      timers.push(timer)
    }

    for (let slotIdx = 0; slotIdx < rotatingSlots; slotIdx++) {
      const initialDelay =
        ROTATE_MS + slotIdx * SLOT_STAGGER_MS + randomBetween(0, ROTATE_JITTER_MS)

      const startTimer = window.setTimeout(() => {
        scheduleSlotRotation(slotIdx)
      }, initialDelay)

      timers.push(startTimer)
    }

    return () => {
      timers.forEach(timer => window.clearTimeout(timer))
    }
  }, [rotatingSlots, shouldRotate])

  const preview = useMemo(() => {
    const rotatingPartners = slotPoolIndices
      .map(index => rotatingPool[index])
      .filter((partner): partner is HomePartnerMiniRecord => partner != null)

    if (featured) {
      return [featured, ...rotatingPartners].slice(0, limit)
    }

    return rotatingPartners.slice(0, limit)
  }, [featured, limit, rotatingPool, slotPoolIndices])

  return (
    <ul className="m-0 grid list-none grid-cols-3 gap-2 p-0">
      {preview.map((partner, slotIndex) => {
        const isFeatured = featured != null && partner.id === featured.id

        return (
          <PartnerMiniCardSlot
            key={slotIndex}
            partner={partner}
            isFeatured={isFeatured}
            hasStripSlot={hasStripSlot}
            animate={shouldRotate}
            onClick={() => navigateTo('/parceiros')}
          />
        )
      })}
    </ul>
  )
}

HomePartnersMiniGrid.displayName = 'HomePartnersMiniGrid'
